import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, RefreshCw, Loader2, MapPin, PenLine, Cake, Vote } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/app/PageHeader";
import { Loader, EmptyState } from "@/components/app/Loader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function Personas() {
  const { hasRole } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["personas"], queryFn: () => api.get("/audience/personas").then((r) => r.data) });

  const regen = useMutation({
    mutationFn: () => api.post("/audience/personas/regenerate").then((r) => r.data),
    onSuccess: () => { toast.success("Personas regenerated from voter file"); qc.invalidateQueries({ queryKey: ["personas"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div>
      <PageHeader
        title="Personas"
        subtitle="Archetypes modeled from your actual voter file, each with concrete, LLM-ready messaging guidance."
        icon={Users}
        actions={
          hasRole("admin", "manager") && (
            <Button data-testid="regenerate-personas-btn" onClick={() => regen.mutate()} disabled={regen.isPending} variant="outline">
              {regen.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Regenerate
            </Button>
          )
        }
      />

      {isLoading ? (
        <Loader />
      ) : !data?.length ? (
        <EmptyState icon={Users} title="No personas yet" description="Import a voter file to model personas." action={<Button asChild className="bg-brand-red text-white"><Link to="/voters">Upload voter file</Link></Button>} />
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {data.map((p, i) => (
            <div key={p.id} className="animate-fade-up rounded-xl border border-border bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md" style={{ animationDelay: `${i * 50}ms` }}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-heading text-lg font-bold text-brand-ink">{p.label}</h3>
                <Badge className="bg-brand-red/10 text-brand-red">{p.size?.toLocaleString()} voters</Badge>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <Mini icon={MapPin} label="Top geo" value={p.top_ward} />
                <Mini icon={Cake} label="Avg age" value={p.avg_age ?? "-"} />
                <Mini icon={Vote} label="Avg support" value={p.avg_support} />
              </div>
              <p className="mt-4 text-sm leading-relaxed text-brand-ink">{p.messaging_guidance}</p>
              <p className="mt-3 border-t border-border pt-3 text-[0.7rem] text-brand-mute">{p.party_mix} · turnout {p.avg_turnout}</p>
              <Button asChild variant="outline" size="sm" className="mt-4 w-full hover:border-brand-red hover:text-brand-red">
                <Link to={`/studio?persona=${p.id}`} data-testid={`use-persona-${p.id}`}><PenLine className="mr-1.5 h-3.5 w-3.5" /> Draft with this persona</Link>
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Mini({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 p-2 text-center">
      <Icon className="mx-auto h-3.5 w-3.5 text-brand-navy" />
      <p className="mt-1 truncate font-heading text-sm font-bold text-brand-ink">{value}</p>
      <p className="text-[0.6rem] uppercase tracking-wider text-brand-mute">{label}</p>
    </div>
  );
}
